import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import tokenManager from '../../managers/identity/TokenManager'
import dataQueryManager, { nillToNull } from '../../managers/network/DataQueryManager'
import { emailify, rutify } from '../apiUtils'

export enum UserRoles {
  EXTERNO = 0,
  GESTOR = 1,
  OPERADOR = 2,
  SUPERVISOR = 3,
  ADMIN = 4,
  INTERINO = 5,
  REPRESENTANTE = 6,
  ESPORADICO = 7,
  // Usuario genérico: identidad sintética por rol_alternativo (no es una persona).
  // No inicia sesión web; solo habilita la credencial del puesto en el torniquete.
  GENERICO = 8,
}

export enum ExternalSpec {
  TRIPULANTE = 12,
  ARMADOR = 11,
  PROVEEDOR = 10,
}

export interface IUser {
  id_usuario: number
  rut: string
  nombre_completo: string
  email: string
  rol: UserRoles
  vigente: number
  notificar: any
  bloqueo: number
  bloqueo_fecha: string
  clave: string
  telefono: string
  id_gestor_fotografia: number
  id_gestor_ci: number
  modo_acceso?: number
}

export const defaultIUser: IUser = {
  id_usuario: 0,
  rut: '',
  nombre_completo: '',
  email: '',
  rol: 0,
  vigente: 1,
  notificar: null,
  bloqueo: 0,
  bloqueo_fecha: '',
  clave: '',
  telefono: '',
  id_gestor_fotografia: 0,
  id_gestor_ci: 0,
  modo_acceso: 1,
}

export interface IUserFiledata {
  id_gestor_fotografia: number
  id_gestor_ci: number
}

export default class UsersServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para usuarios
    this.bucketID = dataStorageManager.registerBucket<IUser>(
      this.getAllUsers.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.USERS, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertUser',
            {
              operation: this.insertUser.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateUser',
            {
              operation: this.updateUser.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updatePassword',
            {
              operation: this.updatePassword.bind(this),
              reloadAfter: false,
            },
          ],
          [
            'updateUserEstado',
            {
              operation: this.updateUserEstado.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllUsers(): Promise<IUser[]> {
    try {
      const response = await dataQueryManager.addQuery<IUser | IUser[]>({
        query: `${import.meta.env.VITE_BASE_URL}/login_rol_menu/1/op_select_usuarios`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })

      if (response.data && Array.isArray(response.data)) {
        return response.data.map((user) => {
          let gestorCI = 0
          let gestorFoto = 0
          if (typeof user.id_gestor_ci === 'number') {
            gestorCI = user.id_gestor_ci
          } else if (typeof user.id_gestor_ci === 'string') {
            gestorCI = parseInt(user.id_gestor_ci)
          }
          if (typeof user.id_gestor_fotografia === 'number') {
            gestorFoto = user.id_gestor_fotografia
          } else if (typeof user.id_gestor_fotografia === 'string') {
            gestorFoto = parseInt(user.id_gestor_fotografia)
          }
          return {
            ...user,
            rut: rutify(user.rut),
            email: emailify(user.email),
            telefono:
              typeof user.telefono === 'string'
                ? nillToNull(user.telefono) ?? ''
                : typeof user.telefono === 'number'
                  ? String(user.telefono)
                  : '',
            id_gestor_ci: gestorCI,
            id_gestor_fotografia: gestorFoto,
            modo_acceso: user.modo_acceso ? parseInt(user.modo_acceso.toString()) : 1,
          }
        })
      } else if (response.data) {
        return [response.data].map((user) => {
          let gestorCI = 0
          let gestorFoto = 0
          if (typeof user.id_gestor_ci === 'number') {
            gestorCI = user.id_gestor_ci
          } else if (typeof user.id_gestor_ci === 'string') {
            gestorCI = parseInt(user.id_gestor_ci)
          }
          if (typeof user.id_gestor_fotografia === 'number') {
            gestorFoto = user.id_gestor_fotografia
          } else if (typeof user.id_gestor_fotografia === 'string') {
            gestorFoto = parseInt(user.id_gestor_fotografia)
          }
          return {
            ...user,
            rut: rutify(user.rut),
            email: emailify(user.email),
            telefono:
              typeof user.telefono === 'string'
                ? nillToNull(user.telefono) ?? ''
                : typeof user.telefono === 'number'
                  ? String(user.telefono)
                  : '',
            id_gestor_ci: gestorCI,
            id_gestor_fotografia: gestorFoto,
            modo_acceso: user.modo_acceso ? parseInt(user.modo_acceso.toString()) : 1,
          }
        })
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  async insertUser(
    userData: Omit<IUser, 'id_usuario' | 'notificar' | 'bloqueo' | 'bloqueo_fecha'>,
  ): Promise<number | false> {
    try {
      const body = {
        op_insert_usuario: {
          rut: rutify(userData.rut),
          password: userData.clave,
          nombre_completo: userData.nombre_completo,
          email: emailify(userData.email),
          rol: userData.rol,
          vigente: userData.vigente,
          telefono: userData.telefono,
          id_gestor_fotografia: parseInt(userData.id_gestor_fotografia.toString()),
          id_gestor_ci: parseInt(userData.id_gestor_ci.toString()),
          modo_acceso: userData.modo_acceso ? parseInt(userData.modo_acceso.toString()) : 1,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/login_rol_menu/1/op_insert_usuario`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      // op_insert_usuario devuelve el id_usuario generado (RETURNING): lo propagamos.
      if (response.data && typeof response.data === 'number') {
        return response.data
      }
    } catch (error) {
      console.error('Error inserting armador:', error)
    }
    return false
  }

  async updateUser(userData: IUser): Promise<boolean> {
    try {
      const body = {
        op_update_usuario: {
          id_usuario: userData.id_usuario,
          rut: rutify(userData.rut),
          nombre_completo: userData.nombre_completo,
          email: emailify(userData.email),
          rol: userData.rol,
          vigente: userData.vigente,
          telefono: userData.telefono,
          id_gestor_fotografia: parseInt(userData.id_gestor_fotografia.toString()),
          id_gestor_ci: parseInt(userData.id_gestor_ci.toString()),
          modo_acceso: userData.modo_acceso ? parseInt(userData.modo_acceso.toString()) : 1,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/login_rol_menu/1/op_update_usuario`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating user:', error)
    }
    return false
  }

  async updateUserEstado(data: { active: number; id_user: number }): Promise<boolean> {
    try {
      const body = {
        op_update_user_estado: {
          active: data.active,
          id_user: data.id_user,
        },
      }
      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_update_user_estado`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') return true
    } catch (error) {
      console.error('Error updating user estado:', error)
    }
    return false
  }

  async updatePassword(password: string, email: string): Promise<boolean> {
    try {
      const body = {
        op_update_cambio_clave: {
          password: password,
          email: emailify(email),
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/login_rol_menu/1/op_update_cambio_clave`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating password:', error)
    }
    return false
  }
}
